import { useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import { hasSameContents } from '../../../../../utils/arrayUtils.js';
import axios from 'axios';
import {
  skillingSheetDropdownOptions,
  uaSkills,
} from '../skillingSheetUtils.js';
import { appInsights } from '../../../../../telemetry/appInsights.js';

export default function EditSkillingSheet({
  isOpen,
  handleClose,
  entry,
  handleRefresh,
  setIsSnackbarOpen,
  setSnackbarMessage,
  setSnackbarSeverity,
  user,
}) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formState, setFormState] = useState({ ...entry });
  const [deleteSelected, setDeleteSelected] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const trackSkillRemoval = data => appInsights.trackEvent({ name: 'SkillRemoval' }, data);
  const trackSkillUpdate = data => appInsights.trackEvent({ name: 'SkillUpdate' }, data);

  const handleUniversalAgentChange = e => {
    const isChecked = e.target.checked;
    const initialValue = entry.ua;

    if (isChecked) {
      // Switch turned ON - set all skills to 1 and clear delete selections
      setDeleteSelected(prev => prev.filter(item => !uaSkills.includes(item)));
      setFormState(prev => {
        const updated = { ...prev };
        uaSkills.forEach(skill => {
          updated[skill] = '1';
        });
        return { ...updated, ua: '1' };
      });
    } else {
      // Switch turned OFF - reset to original values and mark for deletion
      const originalValues = uaSkills.filter(skill => entry[skill]);
      if (initialValue === '1') {
        setDeleteSelected([...originalValues]);
      }
      setFormState(prev => ({
        ...prev,
        ua: "No",
        ...Object.fromEntries(uaSkills.map(skill => [skill, entry[skill]])),
      }));
    }
  };

  const hideDialog = () => {
    setIsDialogOpen(false);
  };

  const hasChanges = () => {
    const { ua: _formStateUa, ...formStateWithoutUa } = formState;
    const { ua: _entryUa, ...entryWithoutUa } = entry;
    return !hasSameContents(
      Object.values(formStateWithoutUa),
      Object.values(entryWithoutUa),
    );
  };

  const calculateChange = () => {
    const changes = [];
    Object.keys(formState).forEach(key => {
      if (formState[key] !== entry[key]) {
        changes.push({ [key]: formState[key] === '1' ? 1 : formState[key] });
      }
    });
    return changes;
  };

  const handleReset = () => {
    setFormState({ ...entry });
    setDeleteSelected([]);
  };

  const removeSkills = () => {
    setIsDialogOpen(true);
  };

  async function removeSkillsConfirmed() {
    try {
      setIsSubmitting(true);
      const response = await axios({
        method: 'DELETE',
        url: `${process.env.REACT_APP_ENDPOINT_ADMIN}/workforce/skilling-sheet/remove`,
        data: { userId: formState.userId, deleteSelected }, // only send the changes instead of the entire entry
      });
      trackSkillRemoval({ skillRemovedBy: user, skillRemovedFor: formState.name, removedSkills: deleteSelected, response, sheetType: 'Retailer' });
      setSnackbarMessage('Skills removed successfully');
      setSnackbarSeverity('success');
      setIsSnackbarOpen(true);
      setIsSubmitting(false);
      handleClose();
      handleRefresh();
    } catch (error) {
      console.error('Error removing skills:', error);
      setSnackbarMessage(
        'There was an error removing the skills. Please try again. If the problem persists, contact support.',
      );
      setSnackbarSeverity('error');
      setIsSnackbarOpen(true);
      setIsSubmitting(false);
    }
  }

  async function submit() {
    try {
      setIsSubmitting(true);
      const calculatedChanges = calculateChange();
      const response = await axios({
        method: 'PATCH',
        url: `${process.env.REACT_APP_ENDPOINT_ADMIN}/workforce/skilling-sheet/update`,
        data: { userId: formState.userId, changes: calculatedChanges }, // only send the changes instead of the entire entry
      });
      trackSkillUpdate({ skillUpdatedBy: user, skillUpdatedFor: formState.name, updatedSkills: calculatedChanges, response, sheetType: 'Retailer' });
      setSnackbarMessage('Retailer skilling sheet updated successfully');
      setSnackbarSeverity('success');
      setIsSnackbarOpen(true);
      setIsSubmitting(false);
      handleClose();
      handleRefresh();
    } catch (error) {
      console.error('Error submitting retailerskilling sheet edit:', error);
      setSnackbarMessage(
        'There was an error submitting the retailer skilling sheet update. Please try again. If the problem persists, contact support.',
      );
      setSnackbarSeverity('error');
      setIsSnackbarOpen(true);
      setIsSubmitting(false);
    }
  }

  return (
    <>
      <Modal
        open={isOpen}
        onClose={handleClose}
        aria-labelledby='modal-modal-title'
        aria-describedby='modal-modal-description'
      >
        <Grid
          container
          spacing={2}
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '95vw', sm: '90vw', md: 800 },
            bgcolor: 'var(--dust-white)',
            p: 4,
            overflowY: 'auto',
            maxHeight: { xs: '90%', md: '90%' },
          }}
        >
          <Grid
            size={12}
            sx={{ display: 'flex', justifyContent: 'space-between' }}
          >
            <Typography variant='h6' component='h2'>
              Edit Skills - {entry.name}
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={formState.ua === '1'}
                  onChange={handleUniversalAgentChange}
                />
              }
              label='Universal Agent'
            />
            <IconButton
              sx={{
                color: 'black',
                '&:hover': {
                  color: 'var(--arcturus-red)',
                },
              }}
              onClick={handleClose}
            >
              <CloseIcon />
            </IconButton>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <Box sx={{ display: 'flex' }}>
              <Checkbox
                icon={<DeleteOutlinedIcon />}
                checkedIcon={<DeleteIcon sx={{ color: 'error.main' }} />}
                sx={{ marginBottom: '20px' }}
                disabled={!entry.spa}
                onChange={e => {
                  if (e.target.checked) {
                    setDeleteSelected([...deleteSelected, 'spa']);
                  } else {
                    setDeleteSelected(
                      deleteSelected.filter(item => item !== 'spa'),
                    );
                  }
                }}
                checked={deleteSelected.includes('spa')}
              />
              <FormControl
                sx={{
                  backgroundColor: '#ffffff',
                  marginBottom: '15px',
                  width: '100%',
                }}
              >
                <InputLabel>{'Spanish'}</InputLabel>
                <Select
                  label={'Spanish'}
                  value={formState.spa || ''}
                  onChange={e =>
                    setFormState({ ...formState, spa: e.target.value })
                  }
                >
                  {skillingSheetDropdownOptions.map(value => {
                    return <MenuItem value={value}>{value}</MenuItem>;
                  })}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ display: 'flex' }}>
              <Checkbox
                icon={<DeleteOutlinedIcon />}
                checkedIcon={<DeleteIcon sx={{ color: 'error.main' }} />}
                sx={{ marginBottom: '20px' }}
                disabled={!entry.disp}
                onChange={e => {
                  if (e.target.checked) {
                    setDeleteSelected([...deleteSelected, 'disp']);
                  } else {
                    setDeleteSelected(
                      deleteSelected.filter(item => item !== 'disp'),
                    );
                  }
                }}
                checked={deleteSelected.includes('disp')}
              />
              <FormControl
                sx={{
                  backgroundColor: '#ffffff',
                  marginBottom: '15px',
                  width: '100%',
                }}
              >
                <InputLabel>{'Dispatch'}</InputLabel>
                <Select
                  label={'Dispatch'}
                  value={formState.disp || ''}
                  onChange={e =>
                    setFormState({ ...formState, disp: e.target.value })
                  }
                >
                  {skillingSheetDropdownOptions.map(value => {
                    return <MenuItem value={value}>{value}</MenuItem>;
                  })}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ display: 'flex' }}>
              <Checkbox
                icon={<DeleteOutlinedIcon />}
                checkedIcon={<DeleteIcon sx={{ color: 'error.main' }} />}
                sx={{ marginBottom: '20px' }}
                disabled={!entry.keys}
                onChange={e => {
                  if (e.target.checked) {
                    setDeleteSelected([...deleteSelected, 'keys']);
                  } else {
                    setDeleteSelected(
                      deleteSelected.filter(item => item !== 'keys'),
                    );
                  }
                }}
                checked={deleteSelected.includes('keys')}
              />
              <FormControl
                sx={{
                  backgroundColor: '#ffffff',
                  marginBottom: '15px',
                  width: '100%',
                }}
              >
                <InputLabel>{'Keys'}</InputLabel>
                <Select
                  label={'Keys'}
                  value={formState.keys || ''}
                  onChange={e =>
                    setFormState({ ...formState, keys: e.target.value })
                  }
                >
                  {skillingSheetDropdownOptions.map(value => {
                    return <MenuItem value={value}>{value}</MenuItem>;
                  })}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ display: 'flex' }}>
              <Checkbox
                icon={<DeleteOutlinedIcon />}
                checkedIcon={<DeleteIcon sx={{ color: 'error.main' }} />}
                sx={{ marginBottom: '20px' }}
                disabled={!entry.chats}
                onChange={e => {
                  if (e.target.checked) {
                    setDeleteSelected([...deleteSelected, 'chats']);
                  } else {
                    setDeleteSelected(
                      deleteSelected.filter(item => item !== 'chats'),
                    );
                  }
                }}
                checked={deleteSelected.includes('chats')}
              />
              <FormControl
                sx={{
                  backgroundColor: '#ffffff',
                  marginBottom: '15px',
                  width: '100%',
                }}
              >
                <InputLabel>{'Chats'}</InputLabel>
                <Select
                  label={'Chats'}
                  value={formState.chats || ''}
                  onChange={e =>
                    setFormState({ ...formState, chats: e.target.value })
                  }
                >
                  {skillingSheetDropdownOptions.map(value => {
                    return <MenuItem value={value}>{value}</MenuItem>;
                  })}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ display: 'flex' }}>
              <Checkbox
                icon={<DeleteOutlinedIcon />}
                checkedIcon={<DeleteIcon sx={{ color: 'error.main' }} />}
                sx={{ marginBottom: '20px' }}
                disabled={!entry.emails}
                onChange={e => {
                  if (e.target.checked) {
                    setDeleteSelected([...deleteSelected, 'emails']);
                  } else {
                    setDeleteSelected(
                      deleteSelected.filter(item => item !== 'emails'),
                    );
                  }
                }}
                checked={deleteSelected.includes('emails')}
              />
              <FormControl
                sx={{
                  backgroundColor: '#ffffff',
                  marginBottom: '15px',
                  width: '100%',
                }}
              >
                <InputLabel>{'Emails'}</InputLabel>
                <Select
                  label={'Emails'}
                  defaultValue={''}
                  value={formState.emails || ''}
                  onChange={e =>
                    setFormState({ ...formState, emails: e.target.value })
                  }
                >
                  {skillingSheetDropdownOptions.map(value => {
                    return <MenuItem value={value}>{value}</MenuItem>;
                  })}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ display: 'flex' }}>
              <Checkbox
                icon={<DeleteOutlinedIcon />}
                checkedIcon={<DeleteIcon sx={{ color: 'error.main' }} />}
                sx={{ marginBottom: '20px' }}
                disabled={!entry.ca}
                onChange={e => {
                  if (e.target.checked) {
                    setDeleteSelected([...deleteSelected, 'ca']);
                  } else {
                    setDeleteSelected(
                      deleteSelected.filter(item => item !== 'ca'),
                    );
                  }
                }}
                checked={deleteSelected.includes('ca')}
              />
              <FormControl
                sx={{
                  backgroundColor: '#ffffff',
                  marginBottom: '15px',
                  width: '100%',
                }}
              >
                <InputLabel>{'California'}</InputLabel>
                <Select
                  label={'California'}
                  value={formState.ca || ''}
                  onChange={e =>
                    setFormState({ ...formState, ca: e.target.value })
                  }
                >
                  {skillingSheetDropdownOptions.map(value => {
                    return <MenuItem value={value}>{value}</MenuItem>;
                  })}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ display: 'flex' }}>
              <Checkbox
                icon={<DeleteOutlinedIcon />}
                checkedIcon={<DeleteIcon sx={{ color: 'error.main' }} />}
                sx={{ marginBottom: '20px' }}
                disabled={!entry.co}
                onChange={e => {
                  if (e.target.checked) {
                    setDeleteSelected([...deleteSelected, 'co']);
                  } else {
                    setDeleteSelected(
                      deleteSelected.filter(item => item !== 'co'),
                    );
                  }
                }}
                checked={deleteSelected.includes('co')}
              />
              <FormControl
                sx={{
                  backgroundColor: '#ffffff',
                  marginBottom: '15px',
                  width: '100%',
                }}
              >
                <InputLabel>{'Colorado'}</InputLabel>
                <Select
                  label={'Colorado'}
                  value={formState.co || ''}
                  onChange={e =>
                    setFormState({ ...formState, co: e.target.value })
                  }
                >
                  {skillingSheetDropdownOptions.map(value => {
                    return <MenuItem value={value}>{value}</MenuItem>;
                  })}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ display: 'flex' }}>
              <Checkbox
                icon={<DeleteOutlinedIcon />}
                checkedIcon={<DeleteIcon sx={{ color: 'error.main' }} />}
                sx={{ marginBottom: '20px' }}
                disabled={!entry.ct}
                onChange={e => {
                  if (e.target.checked) {
                    setDeleteSelected([...deleteSelected, 'ct']);
                  } else {
                    setDeleteSelected(
                      deleteSelected.filter(item => item !== 'ct'),
                    );
                  }
                }}
                checked={deleteSelected.includes('ct')}
              />
              <FormControl
                sx={{
                  backgroundColor: '#ffffff',
                  marginBottom: '15px',
                  width: '100%',
                }}
              >
                <InputLabel>{'Connecticut'}</InputLabel>
                <Select
                  label={'Connecticut'}
                  value={formState.ct || ''}
                  onChange={e =>
                    setFormState({ ...formState, ct: e.target.value })
                  }
                >
                  {skillingSheetDropdownOptions.map(value => {
                    return <MenuItem value={value}>{value}</MenuItem>;
                  })}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ display: 'flex' }}>
              <Checkbox
                icon={<DeleteOutlinedIcon />}
                checkedIcon={<DeleteIcon sx={{ color: 'error.main' }} />}
                sx={{ marginBottom: '20px' }}
                disabled={!entry.fl}
                onChange={e => {
                  if (e.target.checked) {
                    setDeleteSelected([...deleteSelected, 'fl']);
                  } else {
                    setDeleteSelected(
                      deleteSelected.filter(item => item !== 'fl'),
                    );
                  }
                }}
                checked={deleteSelected.includes('fl')}
              />
              <FormControl
                sx={{
                  backgroundColor: '#ffffff',
                  marginBottom: '15px',
                  width: '100%',
                }}
              >
                <InputLabel>{'Florida'}</InputLabel>
                <Select
                  label={'Florida'}
                  value={formState.fl || ''}
                  onChange={e =>
                    setFormState({ ...formState, fl: e.target.value })
                  }
                >
                  {skillingSheetDropdownOptions.map(value => {
                    return <MenuItem value={value}>{value}</MenuItem>;
                  })}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ display: 'flex' }}>
              <Checkbox
                icon={<DeleteOutlinedIcon />}
                checkedIcon={<DeleteIcon sx={{ color: 'error.main' }} />}
                sx={{ marginBottom: '20px' }}
                disabled={!entry.ga}
                onChange={e => {
                  if (e.target.checked) {
                    setDeleteSelected([...deleteSelected, 'ga']);
                  } else {
                    setDeleteSelected(
                      deleteSelected.filter(item => item !== 'ga'),
                    );
                  }
                }}
                checked={deleteSelected.includes('ga')}
              />
              <FormControl
                sx={{
                  backgroundColor: '#ffffff',
                  marginBottom: '15px',
                  width: '100%',
                }}
              >
                <InputLabel>{'Georgia'}</InputLabel>
                <Select
                  label={'Georgia'}
                  value={formState.ga || ''}
                  onChange={e =>
                    setFormState({ ...formState, ga: e.target.value })
                  }
                >
                  {skillingSheetDropdownOptions.map(value => {
                    return <MenuItem value={value}>{value}</MenuItem>;
                  })}
                </Select>
              </FormControl>
            </Box>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <Box sx={{ display: 'flex' }}>
              <Checkbox
                icon={<DeleteOutlinedIcon />}
                checkedIcon={<DeleteIcon sx={{ color: 'error.main' }} />}
                sx={{ marginBottom: '20px' }}
                disabled={!entry.in}
                onChange={e => {
                  if (e.target.checked) {
                    setDeleteSelected([...deleteSelected, 'in']);
                  } else {
                    setDeleteSelected(
                      deleteSelected.filter(item => item !== 'in'),
                    );
                  }
                }}
                checked={deleteSelected.includes('in')}
              />
              <FormControl
                sx={{
                  backgroundColor: '#ffffff',
                  marginBottom: '15px',
                  width: '100%',
                }}
              >
                <InputLabel>{'Indiana'}</InputLabel>
                <Select
                  label={'Indiana'}
                  value={formState.in || ''}
                  onChange={e =>
                    setFormState({ ...formState, in: e.target.value })
                  }
                >
                  {skillingSheetDropdownOptions.map(value => {
                    return <MenuItem value={value}>{value}</MenuItem>;
                  })}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ display: 'flex' }}>
              <Checkbox
                icon={<DeleteOutlinedIcon />}
                checkedIcon={<DeleteIcon sx={{ color: 'error.main' }} />}
                sx={{ marginBottom: '20px' }}
                disabled={!entry.ky}
                onChange={e => {
                  if (e.target.checked) {
                    setDeleteSelected([...deleteSelected, 'ky']);
                  } else {
                    setDeleteSelected(
                      deleteSelected.filter(item => item !== 'ky'),
                    );
                  }
                }}
                checked={deleteSelected.includes('ky')}
              />
              <FormControl
                sx={{
                  backgroundColor: '#ffffff',
                  marginBottom: '15px',
                  width: '100%',
                }}
              >
                <InputLabel>{'Kentucky'}</InputLabel>
                <Select
                  label={'Kentucky'}
                  value={formState.ky || ''}
                  onChange={e =>
                    setFormState({ ...formState, ky: e.target.value })
                  }
                >
                  {skillingSheetDropdownOptions.map(value => {
                    return <MenuItem value={value}>{value}</MenuItem>;
                  })}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ display: 'flex' }}>
              <Checkbox
                icon={<DeleteOutlinedIcon />}
                checkedIcon={<DeleteIcon sx={{ color: 'error.main' }} />}
                sx={{ marginBottom: '20px' }}
                disabled={!entry.ant}
                onChange={e => {
                  if (e.target.checked) {
                    setDeleteSelected([...deleteSelected, 'ant']);
                  } else {
                    setDeleteSelected(
                      deleteSelected.filter(item => item !== 'ant'),
                    );
                  }
                }}
                checked={deleteSelected.includes('ant')}
              />
              <FormControl
                sx={{
                  backgroundColor: '#ffffff',
                  marginBottom: '15px',
                  width: '100%',
                }}
              >
                <InputLabel>{'Antilles'}</InputLabel> {/**TODO */}
                <Select
                  label={'Antilles'}
                  value={formState.ant || ''}
                  onChange={e =>
                    setFormState({ ...formState, ant: e.target.value })
                  }
                >
                  {skillingSheetDropdownOptions.map(value => {
                    return <MenuItem value={value}>{value}</MenuItem>;
                  })}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ display: 'flex' }}>
              <Checkbox
                icon={<DeleteOutlinedIcon />}
                checkedIcon={<DeleteIcon sx={{ color: 'error.main' }} />}
                sx={{ marginBottom: '20px' }}
                disabled={!entry.mi}
                onChange={e => {
                  if (e.target.checked) {
                    setDeleteSelected([...deleteSelected, 'mi']);
                  } else {
                    setDeleteSelected(
                      deleteSelected.filter(item => item !== 'mi'),
                    );
                  }
                }}
                checked={deleteSelected.includes('mi')}
              />
              <FormControl
                sx={{
                  backgroundColor: '#ffffff',
                  marginBottom: '15px',
                  width: '100%',
                }}
              >
                <InputLabel>{'Michigan'}</InputLabel>
                <Select
                  label={'Michigan'}
                  value={formState.mi || ''}
                  onChange={e =>
                    setFormState({ ...formState, mi: e.target.value })
                  }
                >
                  {skillingSheetDropdownOptions.map(value => {
                    return <MenuItem value={value}>{value}</MenuItem>;
                  })}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ display: 'flex' }}>
              <Checkbox
                icon={<DeleteOutlinedIcon />}
                checkedIcon={<DeleteIcon sx={{ color: 'error.main' }} />}
                sx={{ marginBottom: '20px' }}
                disabled={!entry.mn}
                onChange={e => {
                  if (e.target.checked) {
                    setDeleteSelected([...deleteSelected, 'mn']);
                  } else {
                    setDeleteSelected(
                      deleteSelected.filter(item => item !== 'mn'),
                    );
                  }
                }}
                checked={deleteSelected.includes('mn')}
              />
              <FormControl
                sx={{
                  backgroundColor: '#ffffff',
                  marginBottom: '15px',
                  width: '100%',
                }}
              >
                <InputLabel>{'Minnesota'}</InputLabel>
                <Select
                  label={'Minnesota'}
                  value={formState.mn || ''}
                  onChange={e =>
                    setFormState({ ...formState, mn: e.target.value })
                  }
                >
                  {skillingSheetDropdownOptions.map(value => {
                    return <MenuItem value={value}>{value}</MenuItem>;
                  })}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ display: 'flex' }}>
              <Checkbox
                icon={<DeleteOutlinedIcon />}
                checkedIcon={<DeleteIcon sx={{ color: 'error.main' }} />}
                sx={{ marginBottom: '20px' }}
                disabled={!entry.mo}
                onChange={e => {
                  if (e.target.checked) {
                    setDeleteSelected([...deleteSelected, 'mo']);
                  } else {
                    setDeleteSelected(
                      deleteSelected.filter(item => item !== 'mo'),
                    );
                  }
                }}
                checked={deleteSelected.includes('mo')}
              />
              <FormControl
                sx={{
                  backgroundColor: '#ffffff',
                  marginBottom: '15px',
                  width: '100%',
                }}
              >
                <InputLabel>{'Missouri'}</InputLabel>
                <Select
                  label={'Missouri'}
                  value={formState.mo || ''}
                  onChange={e =>
                    setFormState({ ...formState, mo: e.target.value })
                  }
                >
                  {skillingSheetDropdownOptions.map(value => {
                    return <MenuItem value={value}>{value}</MenuItem>;
                  })}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ display: 'flex' }}>
              <Checkbox
                icon={<DeleteOutlinedIcon />}
                checkedIcon={<DeleteIcon sx={{ color: 'error.main' }} />}
                sx={{ marginBottom: '20px' }}
                disabled={!entry.ms}
                onChange={e => {
                  if (e.target.checked) {
                    setDeleteSelected([...deleteSelected, 'ms']);
                  } else {
                    setDeleteSelected(
                      deleteSelected.filter(item => item !== 'ms'),
                    );
                  }
                }}
                checked={deleteSelected.includes('ms')}
              />
              <FormControl
                sx={{
                  backgroundColor: '#ffffff',
                  marginBottom: '15px',
                  width: '100%',
                }}
              >
                <InputLabel>{'Mississippi'}</InputLabel>
                <Select
                  label={'Mississippi'}
                  value={formState.ms || ''}
                  onChange={e =>
                    setFormState({ ...formState, ms: e.target.value })
                  }
                >
                  {skillingSheetDropdownOptions.map(value => {
                    return <MenuItem value={value}>{value}</MenuItem>;
                  })}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ display: 'flex' }}>
              <Checkbox
                icon={<DeleteOutlinedIcon />}
                checkedIcon={<DeleteIcon sx={{ color: 'error.main' }} />}
                sx={{ marginBottom: '20px' }}
                disabled={!entry.nc}
                onChange={e => {
                  if (e.target.checked) {
                    setDeleteSelected([...deleteSelected, 'nc']);
                  } else {
                    setDeleteSelected(
                      deleteSelected.filter(item => item !== 'nc'),
                    );
                  }
                }}
                checked={deleteSelected.includes('nc')}
              />
              <FormControl
                sx={{
                  backgroundColor: '#ffffff',
                  marginBottom: '15px',
                  width: '100%',
                }}
              >
                <InputLabel>{'North Carolina'}</InputLabel>
                <Select
                  label={'North Carolina'}
                  value={formState.nc || ''}
                  onChange={e =>
                    setFormState({ ...formState, nc: e.target.value })
                  }
                >
                  {skillingSheetDropdownOptions.map(value => {
                    return <MenuItem value={value}>{value}</MenuItem>;
                  })}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ display: 'flex' }}>
              <Checkbox
                icon={<DeleteOutlinedIcon />}
                checkedIcon={<DeleteIcon sx={{ color: 'error.main' }} />}
                sx={{ marginBottom: '20px' }}
                disabled={!entry.ne}
                onChange={e => {
                  if (e.target.checked) {
                    setDeleteSelected([...deleteSelected, 'ne']);
                  } else {
                    setDeleteSelected(
                      deleteSelected.filter(item => item !== 'ne'),
                    );
                  }
                }}
                checked={deleteSelected.includes('ne')}
              />
              <FormControl
                sx={{
                  backgroundColor: '#ffffff',
                  marginBottom: '15px',
                  width: '100%',
                }}
              >
                <InputLabel>{'Nebraska'}</InputLabel>
                <Select
                  label={'Nebraska'}
                  value={formState.ne || ''}
                  onChange={e =>
                    setFormState({ ...formState, ne: e.target.value })
                  }
                >
                  {skillingSheetDropdownOptions.map(value => {
                    return <MenuItem value={value}>{value}</MenuItem>;
                  })}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ display: 'flex' }}>
              <Checkbox
                icon={<DeleteOutlinedIcon />}
                checkedIcon={<DeleteIcon sx={{ color: 'error.main' }} />}
                sx={{ marginBottom: '20px' }}
                disabled={!entry.nj}
                onChange={e => {
                  if (e.target.checked) {
                    setDeleteSelected([...deleteSelected, 'nj']);
                  } else {
                    setDeleteSelected(
                      deleteSelected.filter(item => item !== 'nj'),
                    );
                  }
                }}
                checked={deleteSelected.includes('nj')}
              />
              <FormControl
                sx={{
                  backgroundColor: '#ffffff',
                  marginBottom: '15px',
                  width: '100%',
                }}
              >
                <InputLabel>{'New Jersey'}</InputLabel>
                <Select
                  label={'New Jersey'}
                  value={formState.nj || ''}
                  onChange={e =>
                    setFormState({ ...formState, nj: e.target.value })
                  }
                >
                  {skillingSheetDropdownOptions.map(value => {
                    return <MenuItem value={value}>{value}</MenuItem>;
                  })}
                </Select>
              </FormControl>
            </Box>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <Box sx={{ display: 'flex' }}>
              <Checkbox
                icon={<DeleteOutlinedIcon />}
                checkedIcon={<DeleteIcon sx={{ color: 'error.main' }} />}
                sx={{ marginBottom: '20px' }}
                disabled={!entry.ny}
                onChange={e => {
                  if (e.target.checked) {
                    setDeleteSelected([...deleteSelected, 'ny']);
                  } else {
                    setDeleteSelected(
                      deleteSelected.filter(item => item !== 'ny'),
                    );
                  }
                }}
                checked={deleteSelected.includes('ny')}
              />
              <FormControl
                sx={{
                  backgroundColor: '#ffffff',
                  marginBottom: '15px',
                  width: '100%',
                }}
              >
                <InputLabel>{'New York'}</InputLabel>
                <Select
                  label={'New York'}
                  value={formState.ny || ''}
                  onChange={e =>
                    setFormState({ ...formState, ny: e.target.value })
                  }
                >
                  {skillingSheetDropdownOptions.map(value => {
                    return <MenuItem value={value}>{value}</MenuItem>;
                  })}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ display: 'flex' }}>
              <Checkbox
                icon={<DeleteOutlinedIcon />}
                checkedIcon={<DeleteIcon sx={{ color: 'error.main' }} />}
                sx={{ marginBottom: '20px' }}
                disabled={!entry.ri}
                onChange={e => {
                  if (e.target.checked) {
                    setDeleteSelected([...deleteSelected, 'ri']);
                  } else {
                    setDeleteSelected(
                      deleteSelected.filter(item => item !== 'ri'),
                    );
                  }
                }}
                checked={deleteSelected.includes('ri')}
              />
              <FormControl
                sx={{
                  backgroundColor: '#ffffff',
                  marginBottom: '15px',
                  width: '100%',
                }}
              >
                <InputLabel>{'Rhode Island'}</InputLabel>
                <Select
                  label={'Rhode Island'}
                  value={formState.ri || ''}
                  onChange={e =>
                    setFormState({ ...formState, ri: e.target.value })
                  }
                >
                  {skillingSheetDropdownOptions.map(value => {
                    return <MenuItem value={value}>{value}</MenuItem>;
                  })}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ display: 'flex' }}>
              <Checkbox
                icon={<DeleteOutlinedIcon />}
                checkedIcon={<DeleteIcon sx={{ color: 'error.main' }} />}
                sx={{ marginBottom: '20px' }}
                disabled={!entry.sc}
                onChange={e => {
                  if (e.target.checked) {
                    setDeleteSelected([...deleteSelected, 'sc']);
                  } else {
                    setDeleteSelected(
                      deleteSelected.filter(item => item !== 'sc'),
                    );
                  }
                }}
                checked={deleteSelected.includes('sc')}
              />
              <FormControl
                sx={{
                  backgroundColor: '#ffffff',
                  marginBottom: '15px',
                  width: '100%',
                }}
              >
                <InputLabel>{'South Carolina'}</InputLabel>
                <Select
                  label={'South Carolina'}
                  value={formState.sc || ''}
                  onChange={e =>
                    setFormState({ ...formState, sc: e.target.value })
                  }
                >
                  {skillingSheetDropdownOptions.map(value => {
                    return <MenuItem value={value}>{value}</MenuItem>;
                  })}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ display: 'flex' }}>
              <Checkbox
                icon={<DeleteOutlinedIcon />}
                checkedIcon={<DeleteIcon sx={{ color: 'error.main' }} />}
                sx={{ marginBottom: '20px' }}
                disabled={!entry.sd}
                onChange={e => {
                  if (e.target.checked) {
                    setDeleteSelected([...deleteSelected, 'sd']);
                  } else {
                    setDeleteSelected(
                      deleteSelected.filter(item => item !== 'sd'),
                    );
                  }
                }}
                checked={deleteSelected.includes('sd')}
              />
              <FormControl
                sx={{
                  backgroundColor: '#ffffff',
                  marginBottom: '15px',
                  width: '100%',
                }}
              >
                <InputLabel>{'South Dakota'}</InputLabel>
                <Select
                  label={'South Dakota'}
                  value={formState.sd || ''}
                  onChange={e =>
                    setFormState({ ...formState, sd: e.target.value })
                  }
                >
                  {skillingSheetDropdownOptions.map(value => {
                    return <MenuItem value={value}>{value}</MenuItem>;
                  })}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ display: 'flex' }}>
              <Checkbox
                icon={<DeleteOutlinedIcon />}
                checkedIcon={<DeleteIcon sx={{ color: 'error.main' }} />}
                sx={{ marginBottom: '20px' }}
                disabled={!entry.tx}
                onChange={e => {
                  if (e.target.checked) {
                    setDeleteSelected([...deleteSelected, 'tx']);
                  } else {
                    setDeleteSelected(
                      deleteSelected.filter(item => item !== 'tx'),
                    );
                  }
                }}
                checked={deleteSelected.includes('tx')}
              />
              <FormControl
                sx={{
                  backgroundColor: '#ffffff',
                  marginBottom: '15px',
                  width: '100%',
                }}
              >
                <InputLabel>{'Texas'}</InputLabel>
                <Select
                  label={'Texas'}
                  value={formState.tx || ''}
                  onChange={e =>
                    setFormState({ ...formState, tx: e.target.value })
                  }
                >
                  {skillingSheetDropdownOptions.map(value => {
                    return <MenuItem value={value}>{value}</MenuItem>;
                  })}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ display: 'flex' }}>
              <Checkbox
                icon={<DeleteOutlinedIcon />}
                checkedIcon={<DeleteIcon sx={{ color: 'error.main' }} />}
                sx={{ marginBottom: '20px' }}
                disabled={!entry.wa}
                onChange={e => {
                  if (e.target.checked) {
                    setDeleteSelected([...deleteSelected, 'wa']);
                  } else {
                    setDeleteSelected(
                      deleteSelected.filter(item => item !== 'wa'),
                    );
                  }
                }}
                checked={deleteSelected.includes('wa')}
              />
              <FormControl
                sx={{
                  backgroundColor: '#ffffff',
                  marginBottom: '15px',
                  width: '100%',
                }}
              >
                <InputLabel>{'Washington'}</InputLabel>
                <Select
                  label={'Washington'}
                  value={formState.wa || ''}
                  onChange={e =>
                    setFormState({ ...formState, wa: e.target.value })
                  }
                >
                  {skillingSheetDropdownOptions.map(value => {
                    return <MenuItem value={value}>{value}</MenuItem>;
                  })}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ display: 'flex' }}>
              <Checkbox
                icon={<DeleteOutlinedIcon />}
                checkedIcon={<DeleteIcon sx={{ color: 'error.main' }} />}
                sx={{ marginBottom: '20px' }}
                disabled={!entry.wi}
                onChange={e => {
                  if (e.target.checked) {
                    setDeleteSelected([...deleteSelected, 'wi']);
                  } else {
                    setDeleteSelected(
                      deleteSelected.filter(item => item !== 'wi'),
                    );
                  }
                }}
                checked={deleteSelected.includes('wi')}
              />
              <FormControl
                sx={{
                  backgroundColor: '#ffffff',
                  marginBottom: '15px',
                  width: '100%',
                }}
              >
                <InputLabel>{'Wisconsin'}</InputLabel>
                <Select
                  label={'Wisconsin'}
                  value={formState.wi || ''}
                  onChange={e =>
                    setFormState({ ...formState, wi: e.target.value })
                  }
                >
                  {skillingSheetDropdownOptions.map(value => {
                    return <MenuItem value={value}>{value}</MenuItem>;
                  })}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ display: 'flex' }}>
              <Checkbox
                icon={<DeleteOutlinedIcon />}
                checkedIcon={<DeleteIcon sx={{ color: 'error.main' }} />}
                sx={{ marginBottom: '20px' }}
                disabled={!entry.wv}
                onChange={e => {
                  if (e.target.checked) {
                    setDeleteSelected([...deleteSelected, 'wv']);
                  } else {
                    setDeleteSelected(
                      deleteSelected.filter(item => item !== 'wv'),
                    );
                  }
                }}
                checked={deleteSelected.includes('wv')}
              />
              <FormControl
                sx={{
                  backgroundColor: '#ffffff',
                  marginBottom: '15px',
                  width: '100%',
                }}
              >
                <InputLabel>{'West Virginia'}</InputLabel>
                <Select
                  label={'West Virginia'}
                  value={formState.wv || ''}
                  onChange={e =>
                    setFormState({ ...formState, wv: e.target.value })
                  }
                >
                  {skillingSheetDropdownOptions.map(value => {
                    return <MenuItem value={value}>{value}</MenuItem>;
                  })}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ display: 'flex' }}>
              <Checkbox
                icon={<DeleteOutlinedIcon />}
                checkedIcon={<DeleteIcon sx={{ color: 'error.main' }} />}
                sx={{ marginBottom: '20px' }}
                disabled={!entry.tn}
                onChange={e => {
                  if (e.target.checked) {
                    setDeleteSelected([...deleteSelected, 'tn']);
                  } else {
                    setDeleteSelected(
                      deleteSelected.filter(item => item !== 'tn'),
                    );
                  }
                }}
                checked={deleteSelected.includes('tn')}
              />
              <FormControl
                sx={{
                  backgroundColor: '#ffffff',
                  marginBottom: '15px',
                  width: '100%',
                }}
              >
                <InputLabel>{'Tennessee'}</InputLabel>
                <Select
                  label={'Tennessee'}
                  value={formState.tn || ''}
                  onChange={e =>
                    setFormState({ ...formState, tn: e.target.value })
                  }
                >
                  {skillingSheetDropdownOptions.map(value => {
                    return <MenuItem value={value}>{value}</MenuItem>;
                  })}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ display: 'flex' }}>
              <Checkbox
                icon={<DeleteOutlinedIcon />}
                checkedIcon={<DeleteIcon sx={{ color: 'error.main' }} />}
                sx={{ marginBottom: '20px' }}
                disabled={!entry.va}
                onChange={e => {
                  if (e.target.checked) {
                    setDeleteSelected([...deleteSelected, 'va']);
                  } else {
                    setDeleteSelected(
                      deleteSelected.filter(item => item !== 'va'),
                    );
                  }
                }}
                checked={deleteSelected.includes('va')}
              />
              <FormControl
                sx={{
                  backgroundColor: '#ffffff',
                  marginBottom: '15px',
                  width: '100%',
                }}
              >
                <InputLabel>{'Virginia'}</InputLabel>
                <Select
                  label={'Virginia'}
                  value={formState.va || ''}
                  onChange={e =>
                    setFormState({ ...formState, va: e.target.value })
                  }
                >
                  {skillingSheetDropdownOptions.map(value => {
                    return <MenuItem value={value}>{value}</MenuItem>;
                  })}
                </Select>
              </FormControl>
            </Box>
          </Grid>
          <Grid
            size={12}
            sx={{ display: 'flex', justifyContent: 'space-between' }}
          >
            <Tooltip
              title={'This will reset all fields to inital values.'}
              arrow
              placement='top'
            >
              <span>
                <Button
                  variant='contained'
                  sx={{
                    backgroundColor: 'var(--aldebaran-yellow)',
                    color: 'black',
                  }}
                  disabled={!hasChanges() || isSubmitting}
                  onClick={handleReset}
                >
                  Reset
                </Button>
              </span>
            </Tooltip>
            <Tooltip
              title='This will remove any skill selection from selected fields.'
              arrow
              placement='top'
            >
              <span>
                <Button
                  variant='contained'
                  sx={{
                    backgroundColor: 'var(--arcturus-red)',
                  }}
                  onClick={removeSkills}
                  disabled={!deleteSelected.length || isSubmitting}
                >
                  Remove Skills
                </Button>
              </span>
            </Tooltip>
            <Button
              disabled={!hasChanges() || isSubmitting}
              variant='contained'
              sx={{ backgroundColor: 'var(--vega-blue)' }}
              onClick={submit}
            >
              {'Submit'}
            </Button>
          </Grid>
        </Grid>
      </Modal>
      <Dialog open={isDialogOpen} onClose={hideDialog}>
        <DialogTitle>Are you sure you want to remove skills?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This action will reset the selection for the selected skills
          </DialogContentText>
        </DialogContent>
        <DialogActions
          sx={{ display: 'flex', justifyContent: 'space-between' }}
        >
          <Button
            onClick={hideDialog}
            variant='contained'
            sx={{
              backgroundColor: 'var(--aldebaran-yellow)',
              color: 'black',
            }}
          >
            No
          </Button>
          <Button
            onClick={removeSkillsConfirmed}
            variant='contained'
            sx={{
              backgroundColor: 'var(--arcturus-red)',
            }}
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
